<script setup>
import { computed, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'select']);

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const searchQuery = ref('');

const currentUser = useMapGetter('getCurrentUser');
const assignableAgents = useMapGetter(
  'inboxAssignableAgents/getAssignableAgents'
);
const uiFlags = useMapGetter('inboxAssignableAgents/getUIFlags');

const agents = computed(() => {
  const inboxAgents = assignableAgents.value(props.inboxId) || [];
  return inboxAgents.filter(agent => agent.id !== currentUser.value.id);
});

const filteredAgents = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return agents.value;

  return agents.value.filter(agent => {
    const name = (agent.name || agent.available_name || '').toLowerCase();
    const email = (agent.email || '').toLowerCase();
    return name.includes(query) || email.includes(query);
  });
});

watch(
  () => props.show,
  isOpen => {
    if (!isOpen) {
      searchQuery.value = '';
      dialogRef.value?.close();
      return;
    }

    dialogRef.value?.open();
    store.dispatch('inboxAssignableAgents/fetch', [Number(props.inboxId)]);
  }
);

const onSelectAgent = agent => {
  emit('select', agent);
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="$t('CONVERSATION.INTERNAL_MESSAGING.SELECT_AGENT')"
    :show-confirm-button="false"
    :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
    width="md"
    overflow-y-auto
    @close="emit('close')"
  >
    <div class="flex flex-col gap-3">
      <input
        v-model="searchQuery"
        type="search"
        class="w-full"
        :placeholder="$t('CONVERSATION.INTERNAL_MESSAGING.SEARCH_AGENT')"
      />

      <div
        v-if="uiFlags.isFetching"
        class="py-6 text-center text-n-slate-11 text-sm"
      >
        {{ $t('CONVERSATION.LOADING_CONVERSATIONS') }}
      </div>

      <p
        v-else-if="!filteredAgents.length"
        class="py-6 text-center text-n-slate-11 text-sm"
      >
        {{ $t('CONVERSATION.INTERNAL_MESSAGING.NO_AGENTS') }}
      </p>

      <ul v-else class="flex flex-col gap-1 max-h-80 overflow-y-auto">
        <li v-for="agent in filteredAgents" :key="agent.id">
          <button
            type="button"
            class="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-n-alpha-1 text-left"
            @click="onSelectAgent(agent)"
          >
            <Avatar
              :name="agent.name || agent.available_name"
              :src="agent.thumbnail"
              :size="32"
              rounded-full
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ agent.name || agent.available_name }}
            </span>
          </button>
        </li>
      </ul>
    </div>
  </Dialog>
</template>
